import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useBranding, DEFAULT_BRANDING, applyBrandingTheme } from "@/lib/branding";
import { toast } from "sonner";
import { Palette, Save, Plus, CheckCircle2, Trash2, Upload, Sparkles, FileText, Download, Video, Loader2, Music, Volume2 } from "lucide-react";

const FIELDS = [
  { key: "university_name", label: "Name of University", type: "text", ph: "University of Dallas" },
  { key: "short_name", label: "Short Name (sidebar)", type: "text", ph: "Crusaders · Alumni" },
  { key: "tagline", label: "Tag Line", type: "text", ph: "Crusaders · Alumni Registry" },
  { key: "login_headline", label: "Login Headline", type: "text", ph: "Seek Wisdom. Live Abundantly." },
  { key: "login_subtext", label: "Login Subtext", type: "textarea", ph: "The official alumni registry…" },
  { key: "logo_primary", label: "Logo 1 — Seal / Emblem (URL)", type: "text", ph: "https://…/seal.png" },
  { key: "logo_secondary", label: "Logo 2 — Backdrop / Wordmark (URL)", type: "text", ph: "/tower-braniff.png" },
  { key: "university_url", label: "University URL", type: "text", ph: "https://udallas.edu/" },
  { key: "privacy_url", label: "Privacy Notice URL (optional)", type: "text", ph: "https://…/privacy — leave blank to use the built-in notice" },
  { key: "login_bg", label: "Login Background Image (URL)", type: "text", ph: "https://…/campus.jpg" },
  { key: "hero_bg", label: "Hero / Dashboard Background Image (URL)", type: "text", ph: "https://…/campus.jpg" },
];

export default function Branding() {
  const brand = useBranding();
  const [themes, setThemes] = useState([]);
  const [selId, setSelId] = useState("");
  const [f, setF] = useState({ ...DEFAULT_BRANDING, name: "Default" });
  const [busy, setBusy] = useState(false);
  const [aiUrl, setAiUrl] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [docBusy, setDocBusy] = useState(false);
  const [docInfo, setDocInfo] = useState(null);
  const [docToken, setDocToken] = useState("");
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const docHref = (fmt) => `${apiBase}/admin/report-file?fmt=${fmt}&token=${docToken}`;

  const [promo, setPromo] = useState(null);
  const promoHref = (fmt) => `${apiBase}/admin/report-file?fmt=${fmt}&token=${promo?.token || ""}`;

  // Always mint a FRESH short-lived token at click time (avoids "expired token" after a long render).
  const downloadFile = async (fmt) => {
    try {
      const r = await api.get("/admin/report-token");
      const tok = r.data.token;
      if (!tok) { toast.error("Nothing to download yet"); return; }
      window.open(`${apiBase}/admin/report-file?fmt=${fmt}&token=${encodeURIComponent(tok)}`, "_blank", "noopener");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not start download"); }
  };
  const [music, setMusic] = useState({ available: false, name: "" });
  const [musicBusy, setMusicBusy] = useState(false);
  const [prefs, setPrefs] = useState({ voice: "onyx", music_volume: 0.20, music_fade_in: 2.0, music_fade_out: 6.0, voices: ["onyx"] });
  const [previewing, setPreviewing] = useState(false);

  const previewVoice = async () => {
    setPreviewing(true);
    try {
      const r = await api.get(`/admin/voice-preview?voice=${encodeURIComponent(prefs.voice)}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const audio = new Audio(url);
      audio.onended = () => { setPreviewing(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPreviewing(false); toast.error("Could not play the sample"); };
      await audio.play();
    } catch (e) { setPreviewing(false); toast.error(apiError(e.response?.data?.detail) || "Could not generate a voice sample"); }
  };

  const loadPrefs = () => api.get("/admin/promo-prefs").then((r) => setPrefs((p) => ({ ...p, ...r.data }))).catch(() => {});
  const savePrefs = async (patch) => {
    setPrefs((p) => ({ ...p, ...patch }));
    try { await api.put("/admin/promo-prefs", patch); } catch { toast.error("Could not save setting"); }
  };
  const VOICE_LABELS = { onyx: "Onyx — deep male", echo: "Echo — male", fable: "Fable — warm British", alloy: "Alloy — neutral", nova: "Nova — female", shimmer: "Shimmer — bright female" };

  const loadMusic = () => api.get("/admin/promo-music").then((r) => setMusic(r.data)).catch(() => {});
  const uploadMusic = async (file) => {
    if (!file) return;
    setMusicBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/promo-music", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadMusic();
      toast.success("Music uploaded — regenerate the video to mix it in");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setMusicBusy(false); }
  };
  const removeMusic = async () => {
    setMusicBusy(true);
    try { await api.delete("/admin/promo-music"); await loadMusic(); toast.success("Music removed"); }
    catch (e) { toast.error("Could not remove music"); }
    finally { setMusicBusy(false); }
  };

  const loadPromo = () => api.get("/admin/promo-status").then((r) => { setPromo(r.data); return r.data; }).catch(() => null);

  useEffect(() => {
    api.get("/admin/report-token").then((r) => setDocToken(r.data.token || "")).catch(() => {});
    loadPromo();
    loadMusic();
    loadPrefs();
  }, []);

  const anyRunning = promo && (promo["16:9"]?.running || promo["9:16"]?.running);
  useEffect(() => {
    if (!anyRunning) return;
    const iv = setInterval(async () => {
      const d = await loadPromo();
      if (d && !(d["16:9"]?.running || d["9:16"]?.running)) {
        clearInterval(iv);
        if (d["16:9"]?.ok === false || d["9:16"]?.ok === false) toast.error("Video generation failed");
        else toast.success("Marketing video ready");
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [anyRunning]);

  const buildPromo = async (aspect = "16:9") => {
    try {
      await api.get(`/admin/build-promo?aspect=${encodeURIComponent(aspect)}`);
      setPromo((p) => ({ ...(p || {}), [aspect]: { ...((p || {})[aspect] || {}), running: true } }));
      toast.info(`Generating your ${aspect === "9:16" ? "vertical " : ""}marketing video — this takes a couple of minutes.`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not start video generation"); }
  };

  const regenDoc = async () => {
    setDocBusy(true);
    try {
      const { data } = await api.get("/admin/refresh-report");
      setDocToken(data.token || "");
      setDocInfo(data);
      toast.success("Compliance document regenerated with current branding");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not regenerate the document"); }
    finally { setDocBusy(false); }
  };

  const aiFill = async () => {
    if (!aiUrl.trim()) { toast.error("Enter a university URL first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/branding/ai-suggest", { url: aiUrl });
      setF((p) => ({ ...p, ...Object.fromEntries(Object.entries(data).filter(([, v]) => v)) }));
      toast.success("AI filled the theme — review, then Save");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "AI could not read that site"); }
    finally { setAiBusy(false); }
  };

  const load = async (preferId) => {
    try {
      const { data } = await api.get("/branding/themes");
      const list = data.themes || [];
      setThemes(list);
      const pick = list.find((t) => t.id === preferId) || list.find((t) => t.id === selId) || list.find((t) => t.active) || list[0];
      if (pick) { setSelId(pick.id); setF({ ...DEFAULT_BRANDING, ...pick }); }
    } catch (e) { toast.error("Could not load themes"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectTheme = (id) => {
    const t = themes.find((x) => x.id === id);
    if (t) { setSelId(id); setF({ ...DEFAULT_BRANDING, ...t }); }
  };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const current = themes.find((t) => t.id === selId);

  const uploadLogo = async (key, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Store an ENV-AGNOSTIC relative path (not the current preview domain) so branding
      // images resolve correctly after forks/deploys. Rendered directly as <img src>.
      set(key, `/api/files/${data.path}`);
      toast.success("Image uploaded — remember to Save");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Upload failed"); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/branding/themes/${selId}`, f);
      if (data.active) { applyBrandingTheme(data); brand.refresh && brand.refresh(); }
      await load(selId);
      toast.success(data.active ? "Saved — this active theme is now live" : "Theme saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };

  const createTheme = async () => {
    const name = window.prompt("Name for the new theme?", "New Theme");
    if (!name) return;
    try {
      const { data } = await api.post("/branding/themes", { name });
      await load(data.id);
      toast.success(`Created blank theme "${name}" — fill it in, Save, then Activate`);
    } catch (e) { toast.error("Could not create theme"); }
  };

  const activate = async () => {
    try {
      await api.post(`/branding/themes/${selId}/activate`, {});
      applyBrandingTheme(f); brand.refresh && brand.refresh();
      await load(selId);
      toast.success(`"${f.name}" is now the active branding`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not activate"); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete theme "${f.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/branding/themes/${selId}`);
      setSelId("");
      await load();
      toast.success("Theme deleted");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not delete"); }
  };

  return (
    <div className="fade-up max-w-3xl">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-1 flex items-center gap-2"><Palette className="h-7 w-7 text-primary" /> Branding</h1>
        <p className="text-sm text-muted-foreground mt-2">Save multiple branding themes and switch the live one anytime. The active theme applies across the whole platform.</p>
      </div>

      {/* Theme switcher bar */}
      <div className="bg-card border border-border rounded-lg shadow-sm p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="theme-bar">
        <label className="text-sm font-medium">Theme</label>
        <select data-testid="theme-select" value={selId} onChange={(e) => selectTheme(e.target.value)}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm min-w-[200px] focus:ring-2 focus:ring-accent focus:outline-none">
          {themes.map((t) => <option key={t.id} value={t.id}>{t.name}{t.active ? " (active)" : ""}</option>)}
        </select>
        {current?.active
          ? <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase px-2 py-1 rounded bg-emerald-100 text-emerald-800" data-testid="theme-active-badge"><CheckCircle2 className="h-3.5 w-3.5" /> Live</span>
          : <button data-testid="theme-activate" onClick={activate} className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-accent text-accent-foreground text-sm font-medium hover:opacity-90"><CheckCircle2 className="h-4 w-4" /> Set as active</button>}
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="theme-new" onClick={createTheme} className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-sm hover:bg-muted"><Plus className="h-4 w-4" /> New theme</button>
          <button data-testid="theme-delete" onClick={remove} disabled={current?.active} title={current?.active ? "Activate another theme first" : "Delete theme"} className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-sm text-destructive hover:bg-destructive/10 disabled:opacity-40"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm p-6 space-y-5" data-testid="branding-form">
        <div className="rounded-lg border border-accent/40 bg-accent/5 p-4" data-testid="ai-fill">
          <div className="flex items-center gap-2 text-sm font-medium mb-2"><Sparkles className="h-4 w-4 text-accent" /> AI Autofill from a university website</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input data-testid="ai-url" value={aiUrl} onChange={(e) => setAiUrl(e.target.value)} placeholder="e.g. https://udallas.edu"
              onKeyDown={(e) => { if (e.key === "Enter") aiFill(); }}
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
            <button data-testid="ai-fill-btn" onClick={aiFill} disabled={aiBusy}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-accent text-accent-foreground text-sm font-medium hover:opacity-90 disabled:opacity-60">
              <Sparkles className="h-4 w-4" /> {aiBusy ? "Reading site…" : "AI Fill"}
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Captures the name, tagline, colors and logo. Review everything below, then Save.</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Theme Name</label>
          <input data-testid="branding-name" value={f.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Theme name"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-accent focus:outline-none" />
        </div>
        {FIELDS.map((fl) => (
          <div key={fl.key}>
            <label className="block text-sm font-medium mb-1">{fl.label}</label>
            {fl.type === "textarea" ? (
              <textarea data-testid={`branding-${fl.key}`} value={f[fl.key] || ""} onChange={(e) => set(fl.key, e.target.value)} placeholder={fl.ph} rows={3}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
            ) : (
              <input data-testid={`branding-${fl.key}`} value={f[fl.key] || ""} onChange={(e) => set(fl.key, e.target.value)} placeholder={fl.ph}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
            )}
            {["logo_primary", "logo_secondary", "login_bg", "hero_bg"].includes(fl.key) ? (
              <div className="mt-2 flex items-center gap-3">
                {f[fl.key] ? <div className="h-14 w-14 rounded bg-muted flex items-center justify-center p-1 border border-border shrink-0"><img src={f[fl.key]} alt="preview" className="max-h-full max-w-full object-contain" /></div> : null}
                <label data-testid={`upload-${fl.key}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs hover:bg-muted cursor-pointer">
                  <Upload className="h-3.5 w-3.5" /> Upload image
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadLogo(fl.key, e.target.files[0]); e.target.value = ""; }} />
                </label>
                <span className="text-[11px] text-muted-foreground">or paste a URL above</span>
              </div>
            ) : null}
          </div>
        ))}

        <div>
          <label className="block text-sm font-medium mb-1">Privacy Notice text (built-in page)</label>
          <p className="text-xs text-muted-foreground mb-1.5">Shown on the built-in <span className="font-mono">/privacy</span> page and linked from Login &amp; profile-claim. Leave blank to use the default template. Blank lines start a new paragraph; a short line ending without a period is treated as a section heading.</p>
          <textarea data-testid="branding-privacy_notice" value={f.privacy_notice || ""} onChange={(e) => set("privacy_notice", e.target.value)} rows={10}
            placeholder={"Who we are\nThis alumni registry is operated by …\n\nHow we use your information\n…"}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-accent focus:outline-none font-mono" />
        </div>

        <div className="grid grid-cols-2 gap-5">
          {[["primary_color", "Primary / Theme Color"], ["accent_color", "Accent Color"]].map(([k, label]) => (
            <div key={k}>
              <label className="block text-sm font-medium mb-1">{label}</label>
              <div className="flex items-center gap-2">
                <input type="color" data-testid={`branding-${k}`} value={f[k] || "#000000"} onChange={(e) => set(k, e.target.value)} className="h-10 w-14 rounded border border-border bg-background cursor-pointer" />
                <input value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm font-mono" />
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <button data-testid="branding-save" onClick={save} disabled={busy || !selId} className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save Theme"}</button>
          {!current?.active && <button data-testid="branding-save-activate" onClick={async () => { await save(); await activate(); }} className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-border text-sm hover:bg-muted"><CheckCircle2 className="h-4 w-4" /> Save &amp; Activate</button>}
        </div>

        <div className="pt-4 border-t border-border" data-testid="compliance-doc-section">
          <div className="flex items-center gap-2 mb-1"><FileText className="h-4 w-4 text-primary" /><span className="text-sm font-medium">Compliance document</span></div>
          <p className="text-xs text-muted-foreground mb-3">Generate a branded Data Protection & Compliance response (Word + PDF) using this institution's active logo, name, and colours — ready to hand to a prospective customer. Regenerate after changing branding.</p>
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="regen-compliance-doc" onClick={regenDoc} disabled={docBusy} className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60"><Sparkles className="h-4 w-4" /> {docBusy ? "Generating…" : "Regenerate document"}</button>
            <button type="button" data-testid="download-compliance-pdf" onClick={() => downloadFile("pdf")} className="flex items-center gap-2 px-4 py-2 rounded-md border border-border text-sm hover:bg-muted"><Download className="h-4 w-4" /> PDF</button>
            <button type="button" data-testid="download-compliance-docx" onClick={() => downloadFile("docx")} className="flex items-center gap-2 px-4 py-2 rounded-md border border-border text-sm hover:bg-muted"><Download className="h-4 w-4" /> Word</button>
          </div>
          {docInfo && <p className="text-xs text-emerald-700 mt-2">Regenerated {new Date(docInfo.generated_at).toLocaleString()}.</p>}
        </div>

        <div className="pt-4 border-t border-border" data-testid="promo-video-section">
          <div className="flex items-center gap-2 mb-1"><Video className="h-4 w-4 text-primary" /><span className="text-sm font-medium">Marketing video</span></div>
          <p className="text-xs text-muted-foreground mb-3">Generate a branded ~2-minute promo video (with voiceover) built from your Learn More page — cover, features, cost story and a call to action. Uses this institution's active logo, name and colours. Rendering takes a couple of minutes.</p>

          <div className="mb-4 max-w-xs">
            <label className="block text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">Narrator voice</label>
            <div className="flex items-center gap-2">
              <select data-testid="promo-voice" value={prefs.voice} onChange={(e) => savePrefs({ voice: e.target.value })} className="flex-1 px-3 py-2 rounded-md border border-input bg-background text-sm focus:ring-2 focus:ring-accent focus:outline-none">
                {(prefs.voices || []).map((v) => <option key={v} value={v}>{VOICE_LABELS[v] || v}</option>)}
              </select>
              <button data-testid="preview-voice" onClick={previewVoice} disabled={previewing} className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-primary text-primary text-sm hover:bg-primary/5 disabled:opacity-60 whitespace-nowrap">
                {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />} Preview
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Applied on the next Generate/Regenerate.</p>
          </div>

          <div data-testid="promo-music" className="mb-4 p-3 rounded-md bg-muted/40 border border-border">
            <div className="flex items-center gap-2 mb-1"><Music className="h-4 w-4 text-primary" /><span className="text-sm font-medium">Background music</span><span className="text-[11px] text-muted-foreground">(optional)</span></div>
            <p className="text-xs text-muted-foreground mb-2">Upload a track you have the rights to (MP3/WAV/M4A, max 25 MB). It's mixed softly under the voiceover with a fade-out. Regenerate the video after uploading. <strong>Don't upload copyrighted songs you don't have a license for.</strong></p>
            <div className="flex flex-wrap items-center gap-2">
              <label data-testid="upload-promo-music" className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs hover:bg-muted cursor-pointer">
                <Upload className="h-3.5 w-3.5" /> {musicBusy ? "Uploading…" : (music.available ? "Replace music" : "Upload music")}
                <input type="file" accept=".mp3,.wav,.m4a,.aac,.ogg,audio/*" className="hidden" disabled={musicBusy} onChange={(e) => uploadMusic(e.target.files?.[0])} />
              </label>
              {music.available && <span className="text-xs text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Music attached</span>}
              {music.available && <button data-testid="remove-promo-music" onClick={removeMusic} disabled={musicBusy} className="text-xs text-destructive hover:underline">Remove</button>}
            </div>
            <div className="mt-3 max-w-xs">
              <label className="flex items-center justify-between text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">
                <span>Music level under voice</span>
                <span data-testid="music-volume-value" className="text-foreground">{Math.round((prefs.music_volume ?? 0.2) * 100)}%</span>
              </label>
              <input data-testid="music-volume" type="range" min="0.05" max="0.6" step="0.01" value={prefs.music_volume ?? 0.2}
                onChange={(e) => setPrefs((p) => ({ ...p, music_volume: parseFloat(e.target.value) }))}
                onMouseUp={(e) => savePrefs({ music_volume: parseFloat(e.target.value) })}
                onTouchEnd={(e) => savePrefs({ music_volume: parseFloat(e.target.value) })}
                className="w-full accent-primary" />
              <p className="text-[11px] text-muted-foreground">Lower = subtle bed; higher = more present. Applied on the next render.</p>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-4 max-w-md">
              <div>
                <label className="flex items-center justify-between text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1"><span>Fade in</span><span data-testid="fade-in-value" className="text-foreground">{(prefs.music_fade_in ?? 2).toFixed(1)}s</span></label>
                <input data-testid="music-fade-in" type="range" min="0" max="10" step="0.5" value={prefs.music_fade_in ?? 2}
                  onChange={(e) => setPrefs((p) => ({ ...p, music_fade_in: parseFloat(e.target.value) }))}
                  onMouseUp={(e) => savePrefs({ music_fade_in: parseFloat(e.target.value) })}
                  onTouchEnd={(e) => savePrefs({ music_fade_in: parseFloat(e.target.value) })}
                  className="w-full accent-primary" />
              </div>
              <div>
                <label className="flex items-center justify-between text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1"><span>Fade out</span><span data-testid="fade-out-value" className="text-foreground">{(prefs.music_fade_out ?? 6).toFixed(1)}s</span></label>
                <input data-testid="music-fade-out" type="range" min="0" max="12" step="0.5" value={prefs.music_fade_out ?? 6}
                  onChange={(e) => setPrefs((p) => ({ ...p, music_fade_out: parseFloat(e.target.value) }))}
                  onMouseUp={(e) => savePrefs({ music_fade_out: parseFloat(e.target.value) })}
                  onTouchEnd={(e) => savePrefs({ music_fade_out: parseFloat(e.target.value) })}
                  className="w-full accent-primary" />
              </div>
            </div>
          </div>
          {(() => {
            const land = promo?.["16:9"] || {};
            const vert = promo?.["9:16"] || {};
            return (
              <div className="space-y-3">
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">Landscape · 16:9 (web, YouTube, presentations)</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button data-testid="build-promo-video" onClick={() => buildPromo("16:9")} disabled={land.running} className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60"><Video className="h-4 w-4" /> {land.running ? "Generating… (~2 min)" : (land.available ? "Regenerate video" : "Generate video")}</button>
                    <button type="button" data-testid="download-promo-video" onClick={() => land.running ? toast.info("Still rendering — please wait until it finishes") : (land.available ? downloadFile("mp4") : toast.error("Generate the video first"))} disabled={land.running} className={`flex items-center gap-2 px-4 py-2 rounded-md border border-border text-sm hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${!land.available ? "opacity-50" : ""}`}><Download className="h-4 w-4" /> Download MP4</button>
                    {land.running && <span className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Rendering…</span>}
                    {land.available && !land.running && <span className="text-xs text-emerald-700">Ready.</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">Vertical · 9:16 (Instagram, TikTok, Reels)</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button data-testid="build-promo-vertical" onClick={() => buildPromo("9:16")} disabled={vert.running} className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60"><Video className="h-4 w-4" /> {vert.running ? "Generating… (~2 min)" : (vert.available ? "Regenerate vertical" : "Generate vertical")}</button>
                    <button type="button" data-testid="download-promo-vertical" onClick={() => vert.running ? toast.info("Still rendering — please wait until it finishes") : (vert.available ? downloadFile("mp4v") : toast.error("Generate the vertical video first"))} disabled={vert.running} className={`flex items-center gap-2 px-4 py-2 rounded-md border border-border text-sm hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${!vert.available ? "opacity-50" : ""}`}><Download className="h-4 w-4" /> Download MP4</button>
                    {vert.running && <span className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Rendering…</span>}
                    {vert.available && !vert.running && <span className="text-xs text-emerald-700">Ready.</span>}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
